import {
  SEED_MARKETS_MAX,
  SEED_SIZE_FLOOR_KINDS,
  SEED_STAKEHOLDER_TYPES,
  type SeedSizeFloorKind,
  seedAnswersFromCriteria,
  seedAnswersSchema,
} from '@parcel/filters';
import {
  ACCOUNT_TYPE_DEFINITIONS,
  PRIMARY_USE_DEFINITIONS,
  PROJECT_STAGE_DEFINITIONS,
} from '@parcel/types';
import type { Score, SeedScoresInput } from '../../lib/scores.js';

export interface ScoringAnswers {
  markets: string[];
  sectors: string[];
  stages: string[];
  stakeholders: string[];
  sizeFloor: { kind: SeedSizeFloorKind; min: string };
}

const STAKEHOLDERS = new Set<string>(SEED_STAKEHOLDER_TYPES);
export const SECTOR_OPTIONS = PRIMARY_USE_DEFINITIONS;
export const STAGE_OPTIONS = PROJECT_STAGE_DEFINITIONS;
export const STAKEHOLDER_OPTIONS = ACCOUNT_TYPE_DEFINITIONS.filter((d) =>
  STAKEHOLDERS.has(d.value),
);
const SIZE_FLOOR_LABELS: Record<SeedSizeFloorKind, string> = {
  units: 'Residential units',
  sqft: 'Square feet',
  cost: 'Construction cost (USD)',
};
export const SIZE_FLOOR_KINDS = SEED_SIZE_FLOOR_KINDS.map((value) => ({
  value,
  label: SIZE_FLOOR_LABELS[value],
}));

/** Every covered city (none when there are more than one seed takes), housing sectors, the active stages, developers and architects. */
export function defaultScoringAnswers(cities: readonly string[]): ScoringAnswers {
  return {
    markets: cities.length <= SEED_MARKETS_MAX ? [...cities] : [],
    sectors: ['multifamily', 'mixed_use'],
    stages: ['filed', 'under_review', 'approved', 'permitted'],
    stakeholders: ['developer', 'architect'],
    sizeFloor: { kind: 'units', min: '' },
  };
}

/** The floor as a whole number; commas and spaces are allowed while typing. */
function sizeFloorValue(min: string): number | null {
  const digits = min.replace(/[,\s]/g, '');
  if (!/^\d+$/.test(digits)) return null;
  const value = Number(digits);
  return Number.isSafeInteger(value) && value > 0 ? value : null;
}

const FIELD_NAMES: Record<string, string> = {
  markets: 'Focus markets',
  sectors: 'Sectors',
  stages: 'Target stages',
  stakeholders: 'Target stakeholders',
  size_floor: 'Size floor',
};

export function validateScoringAnswers(a: ScoringAnswers): string | null {
  if (a.markets.length === 0) return 'Pick at least one focus market.';
  if (a.markets.length > SEED_MARKETS_MAX) return `Pick at most ${SEED_MARKETS_MAX} focus markets.`;
  if (a.sectors.length === 0) return 'Pick at least one sector.';
  if (a.stages.length === 0) return 'Pick at least one target stage.';
  if (a.stakeholders.length === 0) return 'Pick at least one target stakeholder.';
  if (a.sizeFloor.min.trim() !== '' && sizeFloorValue(a.sizeFloor.min) === null) {
    return 'The size floor must be a whole number above zero, or empty.';
  }
  // The route's own schema has the last word, so nothing it refuses reaches it.
  const checked = seedAnswersSchema.safeParse(toSeedBody(a));
  if (!checked.success) {
    const field = String(checked.error.issues[0]?.path[0] ?? '');
    return `Check ${FIELD_NAMES[field] ?? 'your answers'}: one of them isn't accepted.`;
  }
  return null;
}

export function toSeedBody(a: ScoringAnswers): SeedScoresInput {
  // The form holds plain strings; the route's schema (validateScoringAnswers) checks the enums.
  const body = {
    markets: a.markets,
    sectors: a.sectors,
    stages: a.stages,
    stakeholders: a.stakeholders,
  } as SeedScoresInput;
  const min = sizeFloorValue(a.sizeFloor.min);
  if (min !== null) body.size_floor = { kind: a.sizeFloor.kind, min };
  return body;
}

export interface AnswersReadBack {
  answers: ScoringAnswers;
  /** Whether any answer came from the criteria rather than the defaults. */
  readBack: boolean;
  /** What replacing would remove for good, by name. */
  lost: string[];
}

/** The answers the current criteria encode, so answering again starts where the last seed left off. */
export function answersFromScores(
  scores: readonly Score[] | undefined,
  cities: readonly string[],
): AnswersReadBack {
  const defaults = defaultScoringAnswers(cities);
  const criteriaOf = (key: string) => scores?.find((score) => score.key === key)?.criteria;
  const { answers, lost } = seedAnswersFromCriteria({
    atlas: criteriaOf('atlas'),
    icp: criteriaOf('icp'),
    prospect: criteriaOf('prospect'),
  });
  return {
    answers: {
      markets: answers.markets ?? defaults.markets,
      sectors: answers.sectors ?? defaults.sectors,
      stages: answers.stages ?? defaults.stages,
      stakeholders: answers.stakeholders ?? defaults.stakeholders,
      sizeFloor: answers.size_floor
        ? { kind: answers.size_floor.kind, min: String(answers.size_floor.min) }
        : defaults.sizeFloor,
    },
    readBack: Object.keys(answers).length > 0,
    lost,
  };
}

/** Whether any of the workspace's scores already has criteria a seed would replace. */
export function hasCriteria(scores: readonly Score[] | undefined): boolean {
  return (scores ?? []).some((score) => score.criteria.length > 0);
}
